import cx from "classnames";
import { ReactNode, useCallback, useState } from "react";

import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import ChevronDown from "atoms/icon/icons/chevron_down_small.svg";
import ChevronUp from "atoms/icon/icons/chevron_up_small.svg";
import { Modal } from "atoms/modal/Modal";
import { TextField } from "atoms/text-field/TextField";

import OnboardingHeaderV3 from "../../components/OnboardingScreenHeader/OnboardingHeaderV3";

export const SOURCE_OPTIONS = [
  { id: "employee", label: "From an Employee" },
  { id: "email-list", label: "Email List" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "friend", label: "Through a Friend" },
  { id: "other", label: "Other" },
];

export type SourceFormData = {
  signUpSource: string;
  referrerName: string;
};

export interface SourceFormProps {
  initialSource?: string;
  initialReferrerName?: string;
  onSubmit?: (data: SourceFormData) => void | Promise<void>;
  className?: string;
  submitButtonText?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  disabled?: boolean;
  employeeNameReferralEnabled?: boolean;
}

export const SourceForm = ({
  initialSource = "",
  initialReferrerName = "",
  onSubmit,
  className = "",
  submitButtonText = "Confirm",
  prefix,
  suffix,
  disabled = false,
  employeeNameReferralEnabled = false,
}: SourceFormProps) => {
  const [selectedSourceId, setSelectedSourceId] = useState(initialSource);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [referrerName, setReferrerName] = useState(initialReferrerName);

  const selectedSource = SOURCE_OPTIONS.find(
    (option) => option.id === selectedSourceId
  );

  const handleToggle = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleSourceSelect = useCallback((sourceId: string) => {
    setSelectedSourceId(sourceId);
    setIsModalOpen(false);
  }, []);

  const handleContinue = async () => {
    if (!selectedSourceId || !onSubmit) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onSubmit({ signUpSource: selectedSourceId, referrerName });
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred while saving your information. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSelection = !!selectedSourceId;

  return (
    <>
      {/* Source Selection Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        header={
          <div className="border-b border-gray-200 p-4 text-center">
            <h3 className="font-['DM_Sans'] text-lg font-medium text-[#1b1b1b]">
              Select an option
            </h3>
          </div>
        }
        bodyClassName="p-0"
      >
        <div className="divide-y divide-gray-100 pb-6">
          {SOURCE_OPTIONS.map((source) => (
            <button
              key={source.id}
              onClick={() => handleSourceSelect(source.id)}
              className={cx(
                "w-full px-8 py-4 text-left",
                "font-['DM_Sans'] text-base font-normal leading-[22px]",
                "transition-colors duration-200",
                "active:bg-gray-50",
                selectedSourceId === source.id
                  ? "bg-brand-tint font-medium text-brand-color"
                  : "text-nearly-black hover:bg-gray-50"
              )}
              role="option"
              aria-selected={selectedSourceId === source.id}
            >
              <div className="flex items-center justify-between">
                <span>{source.label}</span>
                {selectedSourceId === source.id && (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-brand-color"
                  >
                    <path
                      d="M16.25 6.25L8.125 14.375L3.75 10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            </button>
          ))}
        </div>
      </Modal>

      <div className={`flex w-full max-w-sm flex-col gap-9 ${className}`}>
        {/* Optional prefix slot for logo */}
        {prefix ? (
          <div className="flex w-full items-center justify-center">
            {prefix}
          </div>
        ) : null}

        {/* Header */}
        {/* gap 12px not gap-12 */}
        <div className="flex flex-col gap-3">
          <OnboardingHeaderV3 title="How did you hear about us?" align="left" />

          <p className="text-body2 text-secondary">
            Choose how you heard about us.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {/* Source Selector Button */}
          <button
            onClick={handleToggle}
            disabled={disabled || isSubmitting}
            className={cx(
              "box-border flex w-full items-center justify-between",
              "h-12 gap-2.5 px-4 py-4",
              "rounded-lg",
              "bg-white",
              "text-body1",
              "transition-colors duration-200 ease-in-out",
              "disabled:opacity-50"
            )}
            aria-expanded={isModalOpen}
            aria-haspopup="listbox"
            style={{ fontVariationSettings: "'opsz' 14" }}
          >
            <span
              className={
                selectedSource ? "text-primary-neutral" : "text-secondary"
              }
            >
              {selectedSource?.label || "Select an option"}
            </span>
            <Icon
              svg={isModalOpen ? ChevronUp : ChevronDown}
              size="small"
              className="opacity-50"
              color="secondaryText"
            />
          </button>

          {/* Employee Name Field - shown when employee is selected */}
          {((selectedSourceId === "employee" && employeeNameReferralEnabled) ||
            selectedSourceId === "other" ||
            selectedSourceId === "friend") && (
            <TextField
              disabled={disabled || isSubmitting}
              type="text"
              label={
                selectedSourceId === "employee"
                  ? "Employee Name"
                  : selectedSourceId === "other"
                  ? "Other"
                  : selectedSourceId === "friend"
                  ? "Friend Name"
                  : undefined
              }
              name="referrerName"
              value={referrerName}
              onChange={(value) => setReferrerName(value)}
            />
          )}
        </div>

        <div className="flex flex-col">
          {submitError && (
            <div className="text-center text-sm text-red">{submitError}</div>
          )}
          <Button
            fullWidth
            disabled={!hasSelection || isSubmitting || disabled}
            onClick={handleContinue}
          >
            {isSubmitting ? "Saving..." : submitButtonText}
          </Button>
        </div>

        {/* Optional suffix slot for extra content */}
        {suffix ? <div className="mt-6 w-full">{suffix}</div> : null}
      </div>
    </>
  );
};
